import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  styled
} from '@mui/material';
import {
  WarningAmberOutlined,
  ErrorOutline,
  InfoOutlined,
  HelpOutlineOutlined,
  CheckCircleOutline
} from '@mui/icons-material';
import DialogTitle from '../atoms/DialogTitle';

/**
 * Confirm dialog types
 */
export type ConfirmType = 'warning' | 'error' | 'info' | 'question';

/**
 * Props for the ConfirmDialog component
 */
export interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message: string;
  type?: ConfirmType;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  showIcon?: boolean;
}

/**
 * Icon mapping for different confirm types
 */
const iconMap = {
  warning: WarningAmberOutlined,
  error: ErrorOutline,
  info: InfoOutlined,
  question: HelpOutlineOutlined,
};

/**
 * Get color for different confirm types from theme
 */
const getColorForType = (theme: any, type: ConfirmType) => {
  const colorMap = {
    warning: theme.palette.error.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main,
    question: theme.palette.primary.main,
  };
  return colorMap[type];
};

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '0 24px 16px 24px',
  '& .MuiTypography-root': {
    color: theme.palette.text.primary,
    fontSize: '14px',
    lineHeight: '1.4',
    fontFamily: 'Open Sans',
  },
}));

const ConfirmButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'confirmType',
})<{ confirmType: ConfirmType }>(({ theme, confirmType }) => ({
  height: '40px',
  padding: '0 24px',
  borderRadius: '6px',
  fontFamily: 'Open Sans',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  minWidth: '100px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  backgroundColor: confirmType === 'info' ? theme.palette.primary.main : getColorForType(theme, confirmType),
  color: theme.palette.primary.contrastText,
  border: 'none',
  '&:hover': {
    backgroundColor: confirmType === 'info' ? theme.palette.primary.dark : getColorForType(theme, confirmType),
    opacity: 0.9,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  height: '40px',
  padding: '0 24px',
  borderRadius: '6px',
  fontFamily: 'Open Sans',
  fontWeight: 600,
  fontSize: '14px',
  textTransform: 'none',
  minWidth: '100px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.text.disabled,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

/**
 * ConfirmDialog component - A replacement for window.confirm() with Material-UI styling
 * 
 * This component provides a clean, accessible dialog for displaying confirmation
 * prompts to users with customizable styling and actions.
 * 
 * @param props - Component props
 * @returns ConfirmDialog component
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  type = 'question',
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  showIcon = true,
}) => {
  const IconComponent = iconMap[type];

  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleConfirm();
    } else if (event.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleCancel();
        }
      }}
      onKeyDown={handleKeyDown}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={false}
      PaperProps={{
        sx: {
          borderRadius: '8px',
          minWidth: '400px',
          backgroundColor: theme => theme.palette.background.paper,
          backgroundImage: 'none',
          border: theme => `0.6px solid ${theme.palette.divider}`,
        },
      }}
      sx={{
        zIndex: 9999, // Ensure dialog and backdrop are above everything
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)', // For Safari support
          zIndex: 9998,
        },
        '& .MuiDialog-container': {
          zIndex: 9999,
        },
        '& .MuiPaper-root': {
          zIndex: 9999,
        }
      }}
    >
      <DialogTitle 
        onClose={handleCancel}
        showIcon={showIcon}
        icon={showIcon ? (
          <IconComponent 
            sx={{ 
              color: theme => getColorForType(theme, type)
            }} 
          />
        ) : undefined}
      >
        {title || type.charAt(0).toUpperCase() + type.slice(1)}
      </DialogTitle>
      
      <StyledDialogContent>
        <Box sx={{ pt: 1 }}>
          <Typography sx={{ 
            margin: 0, 
            color: theme => theme.palette.text.primary, 
            fontSize: '14px', 
            lineHeight: '1.4',
            fontFamily: 'Open Sans'
          }}>
            {message}
          </Typography>
        </Box>
      </StyledDialogContent>
      
      <DialogActions sx={{ 
        display: 'flex',
        gap: '12px',
        padding: '24px',
        paddingTop: '0',
        justifyContent: cancelText ? 'flex-end' : 'center'
      }}>
        {cancelText && (
          <CancelButton
            onClick={handleCancel}
            variant="outlined"
            fullWidth
          >
            {cancelText}
          </CancelButton>
        )}
        <ConfirmButton
          confirmType={type}
          onClick={handleConfirm}
          variant="contained"
          autoFocus
          fullWidth={!!cancelText}
          sx={!cancelText ? { minWidth: '120px' } : {}}
        >
          {confirmText}
        </ConfirmButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
